
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { format, addMonths, subMonths } from "date-fns";
import { useScheduleVisualization } from "@/hooks/useScheduleVisualization";

// Componentes refatorados
import CalendarNavigation from './enhanced-calendar/CalendarNavigation';
import WeekDaysHeader from './enhanced-calendar/WeekDaysHeader';
import CalendarDay from './enhanced-calendar/CalendarDay';
import CalendarLegend from './enhanced-calendar/CalendarLegend';
import LoadingOverlay from './enhanced-calendar/LoadingOverlay';
import { EnhancedCalendarProps } from './enhanced-calendar/types';
import { calculateCalendarRange, generateCalendarDays } from './enhanced-calendar/utils';

const EnhancedCalendar: React.FC<EnhancedCalendarProps> = ({
  psychologistId,
  selectedDate,
  onDateSelect
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const {
    daySchedules,
    isLoading,
    loadScheduleData,
    getScheduleForDate,
    getDayColor,
    getDayTooltip
  } = useScheduleVisualization(psychologistId);

  // Calcular range do calendário apenas quando currentMonth muda
  const calendarRange = useMemo(() => {
    return calculateCalendarRange(currentMonth);
  }, [currentMonth]);

  // Carregar dados apenas quando o range muda
  useEffect(() => {
    if (psychologistId) {
      const { calendarStart, calendarEnd } = calendarRange;
      
      console.log('📅 [EnhancedCalendar] Carregando dados para o mês:', {
        month: format(currentMonth, 'yyyy-MM'),
        start: format(calendarStart, 'yyyy-MM-dd'),
        end: format(calendarEnd, 'yyyy-MM-dd')
      });
      
      loadScheduleData(calendarStart, calendarEnd);
    }
  }, [psychologistId, calendarRange, loadScheduleData]);

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
  }, []);

  const handleDateClick = useCallback((date: Date) => {
    onDateSelect(date);
  }, [onDateSelect]);

  // Gerar dias do calendário com memoização
  const calendarDays = useMemo(() => {
    return generateCalendarDays(
      calendarRange,
      selectedDate,
      getDayColor,
      getDayTooltip,
      getScheduleForDate
    );
  }, [calendarRange, selectedDate, getDayColor, getDayTooltip, getScheduleForDate]);

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 relative">
      <CalendarNavigation 
        currentMonth={currentMonth}
        onNavigateMonth={navigateMonth}
        isLoading={isLoading}
      />

      {/* Quick Navigation */}
      <div className="flex justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={goToToday} className="hover:bg-blue-50 text-blue-600">
          Hoje
        </Button>
      </div>

      <WeekDaysHeader />

      {/* Grid de Dias */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => (
          <CalendarDay
            key={index}
            day={day}
            onDateClick={handleDateClick}
          />
        ))}
      </div>

      <CalendarLegend />

      <LoadingOverlay isLoading={isLoading} />
    </div>
  );
};

export default React.memo(EnhancedCalendar);
